import React from 'react'
import { createRoot } from 'react-dom/client'
import { PostHogProvider } from 'posthog-js/react'
import App from './App.tsx'
import './index.css'

// Suppress harmless warnings, errors, and logs from external CDNs, analytics, and dev tools
if (typeof window !== 'undefined') {
  const originalWarn = console.warn;
  const originalError = console.error;
  const originalLog = console.log;
  
  console.warn = (...args: any[]) => {
    const message = args[0]?.toString() || '';
    // Filter out cookie domain warnings from external CDNs (Cloudflare, etc.)
    if (message.includes('Cookie') && (message.includes('has been rejected') || message.includes('invalid domain') || message.includes('__cf_bm') || message.includes('dmn_chk'))) {
      return; // Suppress cookie warnings
    }
    // Filter out PostHog CORS warnings
    if (message.includes('Cross-Origin Request Blocked') && (message.includes('posthog') || message.includes('posthog.com') || message.includes('i.posthog.com'))) {
      return; // Suppress PostHog CORS warnings
    }
    // Filter out script loading warnings
    if (message.includes('Loading failed') && (message.includes('fbevents.js') || message.includes('gtag/js') || message.includes('gtagmanager.com') || message.includes('facebook.net') || message.includes('ads-twitter.com') || message.includes('uwt.js'))) {
      return; // Suppress script loading warnings
    }
    // Filter out PostHog warnings
    if (message.includes('[PostHog.js]') || message.includes('[PostHog]')) {
      return; // Suppress PostHog warnings
    }
    originalWarn.apply(console, args);
  };
  
  console.error = (...args: any[]) => {
    const message = args[0]?.toString() || '';
    // Filter out PostHog CORS errors
    if (message.includes('Cross-Origin Request Blocked') && (message.includes('posthog') || message.includes('posthog.com') || message.includes('i.posthog.com') || message.includes('eu-assets.i.posthog.com'))) {
      return; // Suppress PostHog CORS errors
    }
    // Filter out script source URI errors
    if (message.includes('source URI is not allowed') || message.includes('error loading dynamically imported module')) {
      return; // Suppress script loading errors
    }
    // Filter out module loading errors for analytics scripts
    if (message.includes('Loading failed') && (message.includes('fbevents.js') || message.includes('gtag/js') || message.includes('gtagmanager.com') || message.includes('posthog') || message.includes('posthog.ts') || message.includes('facebook.net') || message.includes('ads-twitter.com') || message.includes('uwt.js'))) {
      return; // Suppress analytics script loading errors
    }
    // Filter out network errors for external scripts
    if (message.includes('Failed to load resource') || message.includes('net::ERR_') || message.includes('Loading failed for the')) {
      if (message.includes('fbevents.js') || message.includes('gtag') || message.includes('posthog') || message.includes('facebook.net') || message.includes('googletagmanager.com') || message.includes('connect.facebook.net') || message.includes('ads-twitter.com') || message.includes('uwt.js')) {
        return; // Suppress external script loading errors
      }
    }
    // Filter out PostHog network errors (they're handled by retry mechanism)
    if (message.includes('NetworkError') || message.includes('TypeError') || message.includes('fetch')) {
      const fullMessage = args.map(a => a?.toString() || '').join(' ');
      if (fullMessage.includes('[PostHog.js]') || fullMessage.includes('posthog')) {
        return; // Suppress PostHog network errors
      }
    }
    originalError.apply(console, args);
  };
  
  console.log = (...args: any[]) => {
    const message = args[0]?.toString() || '';
    // Filter out Meta Pixel debug messages
    if (message.includes('[Meta Pixel]')) {
      return; // Suppress Meta Pixel logs
    }
    // Filter out Vite HMR messages (only in development)
    if (message.includes('[vite]') || message.includes('[hmr]')) {
      return; // Suppress Vite HMR messages
    }
    // Filter out all PostHog debug messages (they're too verbose)
    if (message.includes('[PostHog.js]') || message.includes('[PostHog]')) {
      return; // Suppress all PostHog logs
    }
    originalLog.apply(console, args);
  };
}

// PostHog configuration (matches official docs structure)
const posthogApiKey = import.meta.env.VITE_PUBLIC_POSTHOG_API_KEY;
const posthogHost = import.meta.env.VITE_PUBLIC_POSTHOG_HOST;

// Check cookie consent preferences
const getCookieConsent = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const preferences = localStorage.getItem('seoro_cookie_preferences');
    if (!preferences) return false;
    const parsed = JSON.parse(preferences);
    return parsed.analytics === true;
  } catch {
    return false;
  }
};

const posthogOptions = {
  api_host: posthogHost || 'https://eu.i.posthog.com',
  // Enable persistence for session tracking (localStorage - doesn't require cookie consent)
  // PostHog uses localStorage by default, which doesn't require cookie consent
  disable_persistence: false, // Enable persistence (localStorage) for session tracking
  // Enable session recordings - record all sessions
  disable_session_recording: false, // ENABLE session recording - record all sessions
  // Session recording configuration
  session_recording: {
    recordCrossOriginIframes: false, // Disable cross-origin iframe recording for security
    maskAllInputs: true, // Mask all input fields for privacy
    maskTextSelector: '[data-ph-mask]', // Mask elements with this selector
  },
  // Disable features that might cause CORS issues
  capture_exceptions: false, // Disable exception capture to avoid CORS errors
  debug: false, // Disable debug mode - too verbose, errors are handled gracefully
  autocapture: true, // Enable autocapture for automatic event tracking
  // Enable decide endpoint for session recordings (needed to load recorder.js)
  // CORS errors will be suppressed in console but recordings need this
  advanced_disable_decide: false, // Enable decide endpoint for session recordings
  // Load callback to handle consent and user identification
  loaded: (posthog: any) => {
    // PostHog uses localStorage (not cookies), so it works WITHOUT cookie consent
    // Always opt in by default - only opt out if user explicitly rejects analytics cookies
    // This ensures session recordings work for all users without requiring cookie acceptance
    
    // Check if user has explicitly rejected analytics cookies
    // If no preference is set OR user hasn't interacted with cookie banner, default to ENABLED
    const hasExplicitlyRejected = (() => {
      try {
        const preferences = localStorage.getItem('seoro_cookie_preferences');
        if (!preferences) return false; // No preference = ENABLE by default (no consent needed)
        const parsed = JSON.parse(preferences);
        // Only opt out if user has EXPLICITLY set analytics to false
        // If user just hasn't accepted yet, we still enable (localStorage doesn't need consent)
        return parsed.analytics === false;
      } catch {
        return false; // Error parsing = ENABLE by default
      }
    })();
    
    // Always opt in unless user explicitly rejected
    // PostHog uses localStorage, not cookies, so GDPR doesn't require consent
    if (hasExplicitlyRejected) {
      posthog.opt_out_capturing();
    } else {
      // Default: ALWAYS opt in - works without cookie consent
      posthog.opt_in_capturing();
    }
    
    // Store posthog instance globally so we can update consent later
    if (typeof window !== 'undefined') {
      (window as any).__posthog = posthog;
    }
    
    // Silent initialization - PostHog handles errors gracefully
    // NetworkErrors are expected in some cases and PostHog retries automatically
    // Events are queued and will be sent when connection is available
  },
  request_batching: true, // Batch requests to reduce CORS issues
} as const;

// Only initialize PostHog if API key is available
const PostHogWrapper = posthogApiKey ? (
  <PostHogProvider apiKey={posthogApiKey} options={posthogOptions}>
    <App />
  </PostHogProvider>
) : (
  <App />
);

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    {PostHogWrapper}
  </React.StrictMode>
)